import { buildApiUrl } from '@/api/baseUrl'
import { getDeviceInfo } from '@/utils/deviceInfo'
import { isNativeApp } from '@/utils/platform'

const ACCESS_TOKEN_KEY = 'gp_access_token'
const REFRESH_TOKEN_KEY = 'gp_refresh_token'
let nativeAccessToken: string | null = null
let nativeRefreshToken: string | null = null
let accessTokenExpiresAt: number | null = null
let sessionEstablishedAt: number | null = null
let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null

const PROACTIVE_REFRESH_LEAD_MS = 2 * 60 * 1000
const FOREGROUND_REFRESH_LEAD_MS = 5 * 60 * 1000
const REFRESH_RETRY_DELAYS_MS = [1000, 2000, 5000]
let now = () => Date.now()
let sleep = (delayMs: number) => new Promise<void>((resolve) => setTimeout(resolve, delayMs))

function emitSessionDiagnostic(
  stage: 'hydrate' | 'refresh' | 'persist' | 'terminate',
  code: string,
  error?: unknown,
): void {
  const statusMatch = String(error ?? '').match(/-?\d+/)
  window.dispatchEvent(new CustomEvent('auth:diagnostic', { detail: {
    stage,
    code,
    session_age_seconds: sessionEstablishedAt
      ? Math.max(0, Math.floor((now() - sessionEstablishedAt) / 1000))
      : null,
    app_version: import.meta.env.VITE_APP_VERSION ?? 'unknown',
    network: navigator.onLine ? 'online' : 'offline',
    keychain_os_status: statusMatch ? Number(statusMatch[0]) : null,
  } }))
}

// ─── Token helpers ────────────────────────────────────────────────────────────

export function getAccessToken(): string | null {
  if (isNativeApp()) return nativeAccessToken
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

export function getRefreshToken(): string | null {
  if (isNativeApp()) return nativeRefreshToken
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

function scheduleProactiveRefresh(): void {
  if (proactiveRefreshTimer) clearTimeout(proactiveRefreshTimer)
  proactiveRefreshTimer = null
  if (!accessTokenExpiresAt || document.visibilityState === 'hidden') return
  const delay = Math.max(0, accessTokenExpiresAt - now() - PROACTIVE_REFRESH_LEAD_MS)
  proactiveRefreshTimer = setTimeout(() => {
    proactiveRefreshTimer = null
    if (document.visibilityState !== 'hidden') void refreshAccessTokenState()
  }, delay)
}

export async function setTokens(
  accessToken: string,
  refreshToken: string,
  expiresIn?: number,
): Promise<void> {
  if (isNativeApp()) {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    // Persist the recoverable credential first. If Keychain is temporarily unavailable,
    // retain both old in-memory and old Keychain values for the server recovery window.
    try {
      await window.__NATIVE_BRIDGE__?.storeRefreshToken?.(refreshToken)
    } catch (error) {
      emitSessionDiagnostic('persist', 'KEYCHAIN_UPDATE_FAILED', error)
      throw error
    }
    nativeAccessToken = accessToken
    nativeRefreshToken = refreshToken
  } else {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  }
  accessTokenExpiresAt = expiresIn && expiresIn > 0 ? now() + expiresIn * 1000 : null
  sessionEstablishedAt ??= now()
  scheduleProactiveRefresh()
}

export async function clearTokens(): Promise<void> {
  if (proactiveRefreshTimer) clearTimeout(proactiveRefreshTimer)
  proactiveRefreshTimer = null
  accessTokenExpiresAt = null
  sessionEstablishedAt = null
  nativeAccessToken = null
  nativeRefreshToken = null
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  if (isNativeApp()) {
    try {
      await window.__NATIVE_BRIDGE__?.clearRefreshToken?.()
    } catch {
      // The in-memory session is already cleared; a revoked server session cannot be refreshed.
    }
  }
}

export async function hydrateNativeSession(): Promise<string | null> {
  const result = await hydrateNativeSessionState()
  return result.kind === 'authenticated' ? result.accessToken : null
}

export type SessionHydrationState =
  | { kind: 'authenticated'; accessToken: string }
  | { kind: 'anonymous' }
  | { kind: 'unavailable' }

export async function hydrateNativeSessionState(): Promise<SessionHydrationState> {
  if (!isNativeApp()) {
    const accessToken = getAccessToken()
    return accessToken ? { kind: 'authenticated', accessToken } : { kind: 'anonymous' }
  }
  if (getAccessToken()) return { kind: 'authenticated', accessToken: getAccessToken()! }
  try {
    const result = await window.__NATIVE_BRIDGE__?.loadRefreshToken?.()
    nativeRefreshToken = result?.refreshToken ?? null
    if (!nativeRefreshToken) return { kind: 'anonymous' }
    return await refreshAccessTokenState()
  } catch {
    emitSessionDiagnostic('hydrate', 'KEYCHAIN_LOAD_FAILED')
    return { kind: 'unavailable' }
  }
}

export function notifyAuthExpired(): void {
  void clearTokens()
  emitAuthLogout()
}

function emitAuthLogout(): void {
  window.dispatchEvent(new CustomEvent('auth:logout'))
}

// ─── ApiError ─────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  code: string
  status: number
  details?: Record<string, unknown>

  constructor(code: string, message: string, status: number, details?: Record<string, unknown>) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.details = details
  }
}

// ─── Internal: token refresh ──────────────────────────────────────────────────

let refreshAccessTokenPromise: Promise<SessionHydrationState> | null = null

type ErrorEnvelope = {
  success?: boolean
  error?: { code?: string; message?: string; details?: Record<string, unknown> }
}

const TERMINAL_REFRESH_REASONS = new Set([
  'expired',
  'revoked',
  'replay',
  'account_disabled',
  'absolute_expired',
  'idle_expired',
  'device_mismatch',
])

async function classifyRefreshFailure(res: Response): Promise<'terminal' | 'retryable'> {
  let body: ErrorEnvelope | null = null
  try {
    body = await res.clone().json() as ErrorEnvelope
  } catch {
    return 'retryable'
  }
  const reason = body?.error?.details?.reason
  if (res.status === 401 && body?.error?.code === 'AUTH_001') {
    return 'terminal'
  }
  if (body?.error?.code === 'AUTH_005' && typeof reason === 'string' && TERMINAL_REFRESH_REASONS.has(reason)) {
    return 'terminal'
  }
  return 'retryable'
}

async function fetchRefresh(refreshToken: string): Promise<Response> {
  const deviceId = getDeviceInfo().device_id
  let response: Response | null = null
  for (let attempt = 0; attempt <= REFRESH_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      response = await fetch(buildApiUrl('/app/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken, device_id: deviceId }),
      })
      if (response.status < 500) return response
    } catch {
      response = null
    }
    if (attempt < REFRESH_RETRY_DELAYS_MS.length) await sleep(REFRESH_RETRY_DELAYS_MS[attempt])
  }
  return response ?? new Response('{}', { status: 503 })
}

async function refreshAccessTokenOnce(): Promise<SessionHydrationState> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    return { kind: 'anonymous' }
  }

  try {
    const res = await fetchRefresh(refreshToken)

    if (!res.ok) {
      if (await classifyRefreshFailure(res) === 'terminal') {
        emitSessionDiagnostic('terminate', 'AUTH_TERMINAL')
        await clearTokens()
        return { kind: 'anonymous' }
      }
      emitSessionDiagnostic('refresh', `HTTP_${res.status}`)
      return { kind: 'unavailable' }
    }

    const json = await res.json()
    if (json.success && json.data?.access_token) {
      const newAccessToken: string = json.data.access_token
      const newRefreshToken: string = json.data.refresh_token ?? refreshToken
      await setTokens(newAccessToken, newRefreshToken, json.data.expires_in)
      return { kind: 'authenticated', accessToken: newAccessToken }
    }

    emitSessionDiagnostic('refresh', 'INVALID_RESPONSE')
    return { kind: 'unavailable' }
  } catch (error) {
    emitSessionDiagnostic('refresh', 'TRANSPORT_OR_PARSE_FAILED', error)
    return { kind: 'unavailable' }
  }
}

export async function refreshAccessTokenState(): Promise<SessionHydrationState> {
  if (refreshAccessTokenPromise) return refreshAccessTokenPromise
  refreshAccessTokenPromise = refreshAccessTokenOnce().finally(() => {
    refreshAccessTokenPromise = null
  })
  return refreshAccessTokenPromise
}

export async function refreshAccessToken(): Promise<string | null> {
  const result = await refreshAccessTokenState()
  return result.kind === 'authenticated' ? result.accessToken : null
}

export async function refreshSessionOnForeground(): Promise<SessionHydrationState> {
  if (!getRefreshToken()) return { kind: 'anonymous' }
  if (!getAccessToken() || !accessTokenExpiresAt || accessTokenExpiresAt - now() < FOREGROUND_REFRESH_LEAD_MS) {
    return refreshAccessTokenState()
  }
  scheduleProactiveRefresh()
  return { kind: 'authenticated', accessToken: getAccessToken()! }
}

export function configureSessionManagerForTests(options?: {
  now?: () => number
  sleep?: (delayMs: number) => Promise<void>
}): void {
  now = options?.now ?? (() => Date.now())
  sleep = options?.sleep ?? ((delayMs) => new Promise<void>((resolve) => setTimeout(resolve, delayMs)))
}

// ─── Core request ─────────────────────────────────────────────────────────────

export async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  options?: {
    body?: unknown
    headers?: Record<string, string>
    skipAuth?: boolean
  },
): Promise<T> {
  const executeRequest = async (accessToken: string | null): Promise<Response> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options?.headers,
    }

    if (!options?.skipAuth && accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const fetchOptions: RequestInit = {
      method,
      headers,
    }

    if (options?.body !== undefined) {
      fetchOptions.body = JSON.stringify(options.body)
    }

    return fetch(buildApiUrl(path), fetchOptions)
  }

  let token = options?.skipAuth ? null : getAccessToken()
  let res = await executeRequest(token)

  // 401 handling: try to refresh once, then retry
  if (res.status === 401 && !options?.skipAuth) {
    const refreshResult = await refreshAccessTokenState()

    if (refreshResult.kind === 'authenticated') {
      res = await executeRequest(refreshResult.accessToken)
    } else if (refreshResult.kind === 'unavailable') {
      throw new ApiError('AUTH_REFRESH_UNAVAILABLE', '暂时无法恢复登录状态，请稍后重试', 503)
    } else {
      emitAuthLogout()
      throw new ApiError('AUTH_001', '登录已过期，请重新登录', 401)
    }

    // Still 401 after refresh attempt — clear tokens and dispatch logout event
    if (res.status === 401) {
      notifyAuthExpired()
      throw new ApiError('AUTH_001', '登录已过期，请重新登录', 401)
    }
  }

  // Parse JSON response
  let json: {
    success: boolean
    data: T
    error?: { code: string; message: string; details?: Record<string, unknown> }
  }
  try {
    json = await res.json()
  } catch {
    throw new ApiError('PARSE_ERROR', 'Failed to parse server response', res.status)
  }

  if (!json.success) {
    const error = json.error
    throw new ApiError(
      error?.code ?? 'UNKNOWN_ERROR',
      error?.message ?? 'An unknown error occurred',
      res.status,
      error?.details,
    )
  }

  return json.data
}
